import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@application/config';
import { map, Observable } from 'rxjs';
import {
  CloseProfileSubscriptionPayload,
  CreateConsumptionReleasePayload,
  ProfileConsumptionRelease,
  ProfileSubscriptionResponse,
  ProfileSubscriptionListItem,
  RenewProfileSubscriptionPayload,
  AddPauseProfileSubscriptionPayload,
  UpdatePauseProfileSubscriptionPayload,
  DeletePauseProfileSubscriptionPayload,
  SwitchProfileSubscriptionPayload,
  SwapOwnershipProfileSubscriptionPayload,
  TransferProfileSubscriptionPayload,
  ESubscriptionsByPersonFieldFilter,
  ProfileOpenReceivable,
} from '../entities/profile-subscription.model';
import {
  TChangeFinancialResponsibleRequest,
  TChangeFinancialResponsibleResponse,
} from '../entities/financial-responsible.model';

function odataConsumptionReleasesFromResponse(
  response: unknown
): ProfileConsumptionRelease[] {
  if (Array.isArray(response)) {
    return response as ProfileConsumptionRelease[];
  }
  if (response && typeof response === 'object' && 'value' in response) {
    const value = (response as { value?: unknown }).value;
    return Array.isArray(value) ? (value as ProfileConsumptionRelease[]) : [];
  }
  return [];
}

@Injectable({ providedIn: 'root' })
export class ProfileSubscriptionService {
  private readonly http = inject(HttpClient);
  private readonly url = inject(APP_CONFIG).apiUrl;
  private readonly apiUrl = inject(APP_CONFIG).apiUrl.subscription_service;
  private readonly odataSubscriptionUrl =
    inject(APP_CONFIG).apiUrl.odata_subscription_service;

  public getSubscriptionsByPerson(
    personId: string,
    filter?: ESubscriptionsByPersonFieldFilter
  ): Observable<ProfileSubscriptionListItem[]> {
    let params = new HttpParams().set('personId', personId);

    if (filter !== undefined && filter !== null) {
      params = params.set('filter', String(filter));
    }

    return this.http.get<ProfileSubscriptionListItem[]>(
      `${this.apiUrl}/Subscription/GetSubscriptionsByPerson`,
      { withCredentials: true, params }
    );
  }

  public deleteSubscriptionById(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/Subscription?id=${id}`, {
      withCredentials: true,
    });
  }

  public deleteSubscriptionRenewals(subscriptionId: string): Observable<void> {
    const params = new HttpParams().set('subscriptionId', subscriptionId);

    return this.http.delete<void>(
      `${this.apiUrl}/Subscription/DeleteLastRenewalsWithoutReceivements`,
      { params, withCredentials: true }
    );
  }

  /**
   * "Excluir renovações" of a finished subscription with automatic renewal
   * scheduled (`POST /Subscription/{id}/CancelAutomaticRenewal`, empty body).
   * Turns `IsAutomaticRenewal` off, clears `NextRenewalScheduleOnUtc` and
   * removes the unpaid future renewals — what
   * `DeleteLastRenewalsWithoutReceivements` does not do, so the renewal job
   * would renew again. Answers the subscription with `DeletedRenewals`; 404
   * when it does not exist or belongs to another tenant.
   */
  public cancelAutomaticRenewal(
    subscriptionId: string
  ): Observable<ProfileSubscriptionResponse> {
    return this.http.post<ProfileSubscriptionResponse>(
      `${this.apiUrl}/Subscription/${subscriptionId}/CancelAutomaticRenewal`,
      null,
      { withCredentials: true }
    );
  }

  public deleteSubscriptionInstallments(
    subscriptionId: string
  ): Observable<void> {
    const params = new HttpParams().set('subscriptionId', subscriptionId);

    return this.http.delete<void>(
      `${this.apiUrl}/Subscription/DeleteInstallmentsWithoutReceivements`,
      { params, withCredentials: true }
    );
  }

  public renewSubscription(
    payload: RenewProfileSubscriptionPayload
  ): Observable<ProfileSubscriptionResponse> {
    return this.http.post<ProfileSubscriptionResponse>(
      `${this.apiUrl}/Subscription/Renew`,
      payload,
      { withCredentials: true }
    );
  }

  public closeSubscription(
    payload: CloseProfileSubscriptionPayload
  ): Observable<ProfileSubscriptionResponse> {
    return this.http.post<ProfileSubscriptionResponse>(
      `${this.apiUrl}/Subscription/Close`,
      payload,
      { withCredentials: true }
    );
  }

  public getSubscriptionById(
    id: string
  ): Observable<ProfileSubscriptionResponse> {
    return this.http.get<ProfileSubscriptionResponse>(
      `${this.apiUrl}/Subscription`,
      {
        params: { id },
        withCredentials: true,
      }
    );
  }

  /**
   * Open account receivables for a subscription, from the receivable-service
   * OData feed. Filters to still-open items (`AmountOpen gt 0`) tied to the
   * subscription and orders by due date. `AccountReceivableId` is the key the
   * receive flow (`openAccountReceivableById`) expects.
   */
  public getOpenReceivablesBySubscription(
    subscriptionId: string
  ): Observable<ProfileOpenReceivable[]> {
    return this.http
      .get<{ value: ProfileOpenReceivable[] }>(
        `${this.url.odata_receivable_service}/AccountsReceivables`,
        {
          params: {
            // The card's ProfileSubscriptionListItem.SubscriptionId value is
            // stored on the receivable's SourceId (AccountReceivableView has no
            // SubscriptionId property, and PersonSubscriptionId does not match
            // this id) — confirmed against the live OData view. So filter by
            // SourceId; SourceType is implicitly ASSINATURA for these ids.
            $filter: `SourceId eq ${subscriptionId} and AmountOpen gt 0`,
            $orderby: 'DueOn',
            $select:
              'AccountReceivableId,PersonId,SourceId,Index,DueOn,AmountOpen,AmountOpenCurrent,AmountInterestCurrent,AmountFineCurrent,DiscountAmount,DiscountType',
          },
          withCredentials: true,
        }
      )
      .pipe(map((response) => response?.value ?? []));
  }

  public addSubscriptionPause(
    payload: AddPauseProfileSubscriptionPayload
  ): Observable<ProfileSubscriptionResponse> {
    return this.http.post<ProfileSubscriptionResponse>(
      `${this.apiUrl}/Subscription/AddPause`,
      payload,
      { withCredentials: true }
    );
  }

  public updateSubscriptionPause(
    payload: UpdatePauseProfileSubscriptionPayload
  ): Observable<ProfileSubscriptionResponse> {
    return this.http.put<ProfileSubscriptionResponse>(
      `${this.apiUrl}/Subscription/UpdatePause`,
      payload,
      { withCredentials: true }
    );
  }

  public deleteSubscriptionPause(
    payload: DeletePauseProfileSubscriptionPayload
  ): Observable<ProfileSubscriptionResponse> {
    return this.http.delete<ProfileSubscriptionResponse>(
      `${this.apiUrl}/Subscription/DeletePause`,
      { body: payload, withCredentials: true }
    );
  }

  public switchSubscription(
    payload: SwitchProfileSubscriptionPayload
  ): Observable<ProfileSubscriptionResponse> {
    return this.http.post<ProfileSubscriptionResponse>(
      `${this.apiUrl}/Subscription/Switch`,
      payload,
      { withCredentials: true }
    );
  }

  public transferSubscription(
    payload: TransferProfileSubscriptionPayload
  ): Observable<ProfileSubscriptionResponse> {
    return this.http.post<ProfileSubscriptionResponse>(
      `${this.apiUrl}/Subscription/Transfer`,
      payload,
      { withCredentials: true }
    );
  }

  public swapOwnership(
    payload: SwapOwnershipProfileSubscriptionPayload
  ): Observable<ProfileSubscriptionResponse> {
    return this.http.post<ProfileSubscriptionResponse>(
      `${this.apiUrl}/Subscription/SwapOwnership`,
      payload,
      { withCredentials: true }
    );
  }

  /**
   * Troca ou retira o responsável financeiro. `FinancialResponsiblePersonId:
   * null` devolve a cobrança ao titular. 404/409 têm tratamento próprio no
   * store; ver `changeFinancialResponsible`.
   */
  public changeFinancialResponsible(
    subscriptionId: string,
    body: TChangeFinancialResponsibleRequest
  ): Observable<TChangeFinancialResponsibleResponse> {
    return this.http.patch<TChangeFinancialResponsibleResponse>(
      `${this.apiUrl}/Subscription/${subscriptionId}/FinancialResponsible`,
      body,
      { withCredentials: true }
    );
  }

  public getConsumptionReleases(
    subscriptionId: string
  ): Observable<ProfileConsumptionRelease[]> {
    const $filter = `SubscriptionId eq ${subscriptionId}`;
    return this.http
      .get<unknown>(`${this.odataSubscriptionUrl}/ConsumptionsReleases`, {
        params: { $filter },
        withCredentials: true,
      })
      .pipe(map(odataConsumptionReleasesFromResponse));
  }

  public createConsumptionRelease(
    payload: CreateConsumptionReleasePayload
  ): Observable<unknown> {
    return this.http.post<unknown>(
      `${this.apiUrl}/ConsumptionRelease`,
      payload,
      { withCredentials: true }
    );
  }

  public deleteConsumptionRelease(
    consumptionReleaseId: string
  ): Observable<unknown> {
    const params = new HttpParams().set('id', consumptionReleaseId);
    return this.http.delete<unknown>(`${this.apiUrl}/ConsumptionRelease`, {
      params,
      withCredentials: true,
    });
  }
}
